import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ReportTemplateResponse } from '@portal/models/reportTemplateResponse';
import { BuilderInfoService } from '@portal/builder';
import LangEnum = ReportTemplateResponse.LangEnum;

@Injectable({
  providedIn: 'root',
})
export class InternalizationService {
  private readonly translation = inject(TranslateService);
  private readonly builderInfoService = inject(BuilderInfoService);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly key = 'language';

  constructor() {
    this.initDefault();
    this.initQueryParamsListener();
  }

  getLanguage(): string | undefined {
    // eslint-disable-next-line no-restricted-globals
    return localStorage.getItem(this.key);
  }

  setLanguage(lang: string) {
    if (lang && lang === this.getLanguage()) {
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    localStorage.setItem(this.key, lang);
    this.translation.use(lang);
    location.reload(); // re init locales
  }

  initDefault() {
    const lang = this.getLanguage() || this.builderInfoService.lang.toLowerCase();
    this.translation.setDefaultLang(lang);
    this.translation.use(lang).subscribe(() => this.exposeCssTranslations());
  }

  private exposeCssTranslations(): void {
    // eslint-disable-next-line no-restricted-globals
    document.documentElement.style.setProperty(
      '--iti-no-results-text',
      `"${this.translation.instant('components.phoneNumber.noResultsFound')}"`,
    );
  }

  initQueryParamsListener(): void {
    this.activatedRoute.queryParams
      .pipe(
        map((params: Params) => params?.lang),
        distinctUntilChanged(),
        filter(
          (lang: string) =>
            !!lang &&
            this.builderInfoService.availableLangs.some(
              availableLang => availableLang.toLowerCase() === lang.toLowerCase(),
            ),
        ),
      )
      .subscribe((lang: LangEnum) => {
        this.setLanguage(lang.toLowerCase());
      });
  }
}
